
import {
  BackupScheduleDto,
  BackupScheduleId,
  TaskScheduleDto,
  TaskScheduleRepositoryInterface,
} from '@/services/Backup/interfaces/index';
import { AxiosError } from 'axios';
import { Component, Vue } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import Themeable from 'vuetify/lib/mixins/themeable';
import DeleteBackupScheduleDialog from '@/components/dialogs/backups/DeleteBackupScheduleDialog.vue';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    DeleteBackupScheduleDialog,
  },
  inject: [
    'taskScheduleRepository',
  ],
})
export default class AllTaskSchedules extends Vue {
  public loading: boolean = false;
  public currentPage: number = 1;
  public itemsPerPage: number = 10;
  public totalItems: number = 0;

  public taskScheduleRepository: TaskScheduleRepositoryInterface;

  public deleteDialog: string = '';
  public deleteDialogData: BackupScheduleDto | null = null;

  public backupSchedules: TaskScheduleDto[] = [];

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Target Server', value: 'targetServerId' },
    { text: 'Interval Days', value: 'intervalDays' },
    { text: 'Run At Hour', value: 'runAtHour' },
    { text: 'Run At Minute', value: 'runAtMinute' },
    { text: 'Last Run Day', value: 'lastRunDay' },
    { text: 'Retries', value: 'retries' },
    { text: 'Created At', value: 'createdAt' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public searchOptions = {};

  public async initialize(): Promise<void> {
    await this.search();
  }

  public async created(): Promise<void> {
    await this.initialize();
  }

  public async search(): Promise<void> {
    this.loading = true;
    try {
      await this.getBackupSchedules();
    } catch (err) {
      this.onError(err);
    } finally {
      this.loading = false;
    }
  }

  public async getBackupSchedules(): Promise<void> {
    const result = await this.taskScheduleRepository.searchTaskSchedules(undefined, {
      page: this.currentPage,
      pageSize: this.itemsPerPage,
    });

    this.totalItems = result?.total ?? 0;
    this.backupSchedules = (result?.data ?? []).map(x => ({
      ...x,
      idComposite: x.type + "_" + x.id,
    }));
  }

  public async changePage(): Promise<void> {
    await this.search();
  }

  public async showDeleteDialogFor(id: BackupScheduleId): Promise<void> {
    try {
      const backupSchedule = this.backupSchedules.find(x => x.id === id);
      if (!backupSchedule) {
        return;
      }

      this.deleteDialog = 'delete-backup-schedule-dialog';
      this.deleteDialogData = backupSchedule;
    } catch (err) {
      this.onError(err);
    }
  }

  public async createTaskSchedule(): Promise<void> {
    await this.$router.push({ name: 'schedule_create' });
  }

  public async deleteBackupSchedule(backupSchedule: BackupScheduleDto): Promise<void> {
    try {
      await this.backupScheduleRepository.deleteBackupSchedule(backupSchedule.id);

      this.$successDialog.show('Backup schedule deleted successfully');

      await this.search();
    } catch (err) {
      this.onError(err);
    }
  }

  private onError(err: unknown): void {
    const asAxiosError = err as AxiosError;
    if (asAxiosError.isAxiosError) {
      this.$errorDialog.show(asAxiosError.response?.data?.message ?? 'An axios error occurred');
    } else if (err instanceof Error) {
      this.$errorDialog.show(err.message);
    } else {
      this.$errorDialog.show('An unknown error occurred');
    }
  }
}
